import React from 'react';
import PropTypes from 'prop-types';
import { Menu, MenuButton, MenuItems, MenuItem } from '@headlessui/react';
import Button from '@common/components/Button';
import * as styles from './DropdownMenu.module.css';

/**
 * DropdownMenu Component
 *
 * A generic dropdown menu that displays a button which opens a menu with items.
 * Built on Headless UI for accessibility and keyboard navigation.
 *
 * @param {string} label - The button label text
 * @param {React.ReactNode} icon - Optional icon to display in the button
 * @param {Array} items - Array of menu items with { key, label, icon, onClick, disabled }
 * @param {string} className - Optional additional CSS class for the container
 * @param {string} buttonClassName - Optional additional CSS class for the button
 * @param {boolean} disabled - Whether the dropdown button is disabled
 * @param {string} variant - Button variant (default: 'outline')
 * @param {string} size - Button size (default: 'small')
 *
 * @example
 * // Simple dropdown
 * <DropdownMenu
 *   label="Actions"
 *   items={[
 *     { key: 'edit', label: 'Edit', onClick: handleEdit },
 *     { key: 'delete', label: 'Delete', onClick: handleDelete },
 *   ]}
 * />
 *
 * @example
 * // Export dropdown with icons
 * <DropdownMenu
 *   label={t('dashboard.export')}
 *   icon={<DownloadIcon size={14} />}
 *   items={[
 *     { key: 'pdf', label: t('dashboard.exportPdf'), icon: <PdfIcon size={16} />, onClick: handlePdfExport },
 *     { key: 'excel', label: t('dashboard.exportExcel'), icon: <ExcelIcon size={16} />, onClick: handleExcelExport },
 *   ]}
 * />
 */
export default function DropdownMenu({
  label,
  icon,
  items = [],
  className = '',
  buttonClassName = '',
  disabled = false,
  variant = 'outline',
  size = 'small',
}) {
  return (
    <Menu as="div" className={`${styles.container} ${className}`}>
      <MenuButton as={Button} variant={variant} size={size} disabled={disabled} className={buttonClassName}>
        {icon}
        {label}
      </MenuButton>
      <MenuItems className={styles.menu}>
        {items.map((item) => (
          <MenuItem key={item.key} disabled={item.disabled}>
            {({ active, disabled: isDisabled }) => (
              <button
                type="button"
                className={`${styles.menuItem} ${isDisabled ? styles.menuItemDisabled : ''} ${active ? styles.menuItemActive : ''}`}
                onClick={item.onClick}
                disabled={isDisabled}
              >
                {item.icon && <span className={styles.menuItemIcon}>{item.icon}</span>}
                <span>{item.label}</span>
              </button>
            )}
          </MenuItem>
        ))}
      </MenuItems>
    </Menu>
  );
}

DropdownMenu.propTypes = {
  label: PropTypes.node.isRequired,
  icon: PropTypes.node,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      icon: PropTypes.node,
      onClick: PropTypes.func,
      disabled: PropTypes.bool,
    }),
  ),
  className: PropTypes.string,
  buttonClassName: PropTypes.string,
  disabled: PropTypes.bool,
  variant: PropTypes.string,
  size: PropTypes.string,
};

DropdownMenu.defaultProps = {
  icon: null,
  items: [],
  className: '',
  buttonClassName: '',
  disabled: false,
  variant: 'outline',
  size: 'small',
};
